import { getData } from "../../api/api";
import type { RdtLikeColumn } from "../ui/rdtLikeToDataGridColumns";

export type ReadQueryParams = {
  page?: number;
  size?: number;
  sort?: string;
  search?: string;
  fields?: string | string[];
};

const ROW_PROPERTY_PATTERN = /row(?:\?\.)?(?:\[['"](\w+)['"]\]|\.(\w+))/;

/** Minified bundles rename `row` to single-letter params, e.g. `(e=>e.title`. */
const GENERIC_SELECTOR_PROPERTY_PATTERN =
  /[a-zA-Z_$][\w$]*(?:\?\.)?(?:\[['"](\w+)['"]\]|\.(\w+))/g;

const IGNORED_SELECTOR_PROPERTIES = new Set(["format", "replace", "toString", "trim"]);

export const normalizeFieldsList = (fields?: string | string[]): string[] => {
  if (!fields) {
    return [];
  }

  const raw = Array.isArray(fields) ? fields.join(",") : fields;
  return raw
    .split(",")
    .map((field) => field.trim())
    .filter(Boolean);
};

export const toFieldsParam = (fields?: string | string[]): string | undefined => {
  const list = normalizeFieldsList(fields);
  if (!list.length) {
    return undefined;
  }

  return Array.from(new Set(list)).join(",");
};

export const buildReadQueryString = (params: ReadQueryParams): string => {
  const searchParams = new URLSearchParams();

  if (params.page != null) {
    searchParams.set("page", String(params.page));
  }
  if (params.size != null) {
    searchParams.set("size", String(params.size));
  }
  if (params.sort) {
    searchParams.set("sort", params.sort);
  }
  if (params.search) {
    searchParams.set("search", params.search);
  }

  const fields = toFieldsParam(params.fields);
  if (fields) {
    searchParams.set("fields", fields);
  }

  return searchParams.toString();
};

export const buildReadUrl = (readPath: string, params: ReadQueryParams = {}): string => {
  const query = buildReadQueryString(params);
  if (!query) {
    return readPath;
  }

  return readPath.includes("?") ? `${readPath}&${query}` : `${readPath}?${query}`;
};

export const appendFieldsToSearchParams = (
  params: URLSearchParams,
  fields?: string | string[],
): URLSearchParams => {
  const value = toFieldsParam(fields);
  if (value) {
    params.set("fields", value);
  }
  return params;
};

export const extractFieldFromSelector = (selector?: (row: unknown) => unknown): string | null => {
  if (!selector) {
    return null;
  }

  const source = selector.toString();

  const rowMatch = ROW_PROPERTY_PATTERN.exec(source);
  ROW_PROPERTY_PATTERN.lastIndex = 0;
  if (rowMatch) {
    return rowMatch[1] || rowMatch[2] || null;
  }

  for (const match of source.matchAll(GENERIC_SELECTOR_PROPERTY_PATTERN)) {
    const field = match[1] || match[2];
    if (field && !IGNORED_SELECTOR_PROPERTIES.has(field)) {
      return field;
    }
  }

  return null;
};

export const extractTableFieldsFromColumns = (
  cols: RdtLikeColumn[],
  extraFields: string[] = [],
): string[] => {
  const fields = new Set<string>(["id", "createdDate", ...extraFields]);

  for (const col of cols) {
    if (col.field) {
      fields.add(col.field);
      continue;
    }

    const extracted = extractFieldFromSelector(col.selector);
    if (extracted) {
      fields.add(extracted);
    }
  }

  return Array.from(fields);
};

export const isPaginatedReadUrl = (fetchUrl?: string): boolean =>
  Boolean(fetchUrl && /\/read\/?$/.test(fetchUrl.replace(/\?.*$/, "")));

export const buildReadRecordUrl = (fetchUrl: string, id: string): string => {
  const base = fetchUrl.replace(/\?.*$/, "").replace(/\/$/, "");
  return `${base}/${encodeURIComponent(id)}`;
};

export const fetchReadRecordById = async <T = unknown>(
  fetchUrl: string,
  id: string,
): Promise<T> => {
  return getData(buildReadRecordUrl(fetchUrl, id)) as Promise<T>;
};
